import { AccountPlan, PricingPlanTag, Subscription } from '@/constants/enum';
import { camelToRegularText, capitalizeFirstLetter, formatNumberWithFixedDecimals, handleOpenChat, CONSTANT } from '@/helpers';
import { useGetUserPlanQuery } from '@/redux/api/api.caller';
import { DiscountType } from '@/types/account';
import { IPricingPlanData } from '@/types/dashboard';
import { Badge, BlockStack, Box, Button, Icon, InlineStack, Link, List, Text, useBreakpoints } from '@shopify/polaris';
import { CheckIcon, InfoIcon, ThumbsUpIcon } from '@shopify/polaris-icons';
import { Fragment, memo, useMemo } from 'react';
import AssistantIcon from '@/components/TrakowAssistantButton/AssistantIcon';
import {
  getExtraQuotaFromTotalQuotaPerMonth,
  getMonthlyQuantity,
  getRangeQuotaForAllPlans,
  getSubscriptionText,
  sortedPlansByTier,
  UNLIMITED_QUOTA,
} from '../../config';
import DescriptionTooltip from '../DescriptionTooltip';
import { checkIsHigherTierPlan, getChoosePlanLabel, getRecommendedPlanByQuota } from './config';
import { StyledPlanCard } from './styled';
interface PlanCardProps {
  previousDisplayPlan: string | null;
  totalQuota: number;
  planInfo: IPricingPlanData;
  handleUpdatePlan: () => void;
  isBtnLoading?: boolean;
  isUpgradePlan: boolean;
  activeSubscription: Subscription;
}

const PlanCard = ({
  previousDisplayPlan,
  totalQuota,
  handleUpdatePlan,
  isBtnLoading,
  isUpgradePlan = false,
  activeSubscription,
  planInfo,
}: PlanCardProps) => {
  const {
    plan,
    tag,
    annuallyDiscountPercent,
    displayName,
    description,
    features,
    quotaPerMonth,
    extraQuota: extraQuotaInformation,
    annualMultiplier,
    usagePricePerTracking,
    usageMaxOverageTracking,
  } = planInfo;
  const { data } = useGetUserPlanQuery({});
  const { smDown } = useBreakpoints();
  const isHighestPlan = plan === sortedPlansByTier[sortedPlansByTier.length - 1];
  const isFreePlan = plan === AccountPlan.Starter;
  const havingPriceTag = !!tag && tag !== PricingPlanTag.NONE;
  const quotaRange = getRangeQuotaForAllPlans(data?.data.allPlans || [], activeSubscription);
  const renderedButtonContainer = useMemo(() => {
    if (data) {
      const {
        plan: currentPlan,
        subscription: currentSubscription,
        quotaPerMonth: currentQuotaPerMonth,
        extraQuotaPerMonth: currentExtraQuotaPerMonth,
      } = data.data;
      const monthlyQuantity = getMonthlyQuantity(currentPlan, currentSubscription);
      const isActivePlan =
        (currentPlan === plan && currentSubscription === activeSubscription) ||
        (currentPlan === AccountPlan.Starter && plan === AccountPlan.Starter);
      const isSameQuota =
        currentQuotaPerMonth !== UNLIMITED_QUOTA &&
        Number(currentQuotaPerMonth) + currentExtraQuotaPerMonth === totalQuota / monthlyQuantity;
      const isLowerTierPlan = !checkIsHigherTierPlan(quotaRange, totalQuota, plan);

      return isActivePlan && isSameQuota ? (
        <Badge progress="complete" tone="info">
          Your plan
        </Badge>
      ) : (
        <Button
          onClick={() => handleUpdatePlan()}
          variant={havingPriceTag ? 'primary' : undefined}
          loading={isBtnLoading}
          disabled={isBtnLoading || isLowerTierPlan}
          size="large"
        >
          {isLowerTierPlan ? 'Over plan limit' : getChoosePlanLabel(data.data.trialInfo, plan, activeSubscription)}
        </Button>
      );
    }
    return null;
  }, [data, plan, activeSubscription, totalQuota, quotaRange, handleUpdatePlan, isBtnLoading, havingPriceTag]);

  const renderedPrice = useMemo(() => {
    if (data) {
      const { monthlyPricing, yearlyPricing, discount, discountPlanApply } = data.data;
      const priceData = {
        [Subscription.Monthly]: monthlyPricing[plan],
        [Subscription.Annually]: yearlyPricing[plan],
      };
      const discountData = discount.filter((item) => [DiscountType.Fixed, DiscountType.Percent].includes(item.type));
      const extraQuotaQuantityPerMonth = getExtraQuotaFromTotalQuotaPerMonth(plan, activeSubscription, quotaRange, totalQuota);
      const extraQuotaPricePerMonth =
        extraQuotaInformation.find((item) => item.additionalAmount === extraQuotaQuantityPerMonth)?.additionalPrice || 0;
      const extraQuotaPrice =
        activeSubscription === Subscription.Annually ? extraQuotaPricePerMonth * annualMultiplier : extraQuotaPricePerMonth;

      // annually case
      if (activeSubscription === Subscription.Annually) {
        const paidPercent = 1 - annuallyDiscountPercent / 100;
        return {
          discountPercent: annuallyDiscountPercent,
          discountCardTitle: `${annuallyDiscountPercent}% OFF`,
          priceBeforeDiscount: `$${formatNumberWithFixedDecimals(priceData.monthly * annualMultiplier + extraQuotaPrice)}`,
          priceAfterDiscount: `$${formatNumberWithFixedDecimals(
            priceData.annually * annualMultiplier + extraQuotaPrice * paidPercent,
          )}`,
        };
      }

      // not discount for monthly case
      if (!discountData.length || !discountPlanApply.includes(plan)) {
        return {
          priceAfterDiscount: `$${formatNumberWithFixedDecimals(priceData.monthly + extraQuotaPrice)}`,
        };
      }

      // discount for monthly case
      const { value: monthlyDiscountValue, type: discountType } = discountData?.[0];
      if (discountType === DiscountType.Percent) {
        return {
          discountPercent: Math.floor(monthlyDiscountValue || 0),
          discountCardTitle: `${Math.floor(monthlyDiscountValue || 0)}% OFF`,
          priceBeforeDiscount: `$${formatNumberWithFixedDecimals(priceData.monthly + extraQuotaPrice)}`,
          priceAfterDiscount: `$${formatNumberWithFixedDecimals(
            (priceData.monthly + extraQuotaPrice) * (1 - (monthlyDiscountValue || 0) / 100),
          )}`,
        };
      }

      // another case (fixed discount,...)
      return {
        discountPercent: (monthlyDiscountValue || 0).toFixed(2),
        discountCardTitle: `${(monthlyDiscountValue || 0).toFixed(2)}`,
        priceBeforeDiscount: `$${formatNumberWithFixedDecimals(priceData.monthly + extraQuotaPrice)}`,
        priceAfterDiscount: `$${formatNumberWithFixedDecimals(
          priceData.monthly + extraQuotaPrice - (monthlyDiscountValue || 0),
        )}`,
      };
    }
    return undefined;
  }, [data, plan, quotaRange, totalQuota, extraQuotaInformation, activeSubscription, annuallyDiscountPercent, annualMultiplier]);

  const renderQuotaQuantity = () => {
    if (plan === AccountPlan.Starter) {
      return Number(quotaPerMonth);
    }

    const { min, max } = quotaRange[plan];
    if (totalQuota <= min) {
      return min;
    }
    if (totalQuota >= max) {
      return max;
    }
    return totalQuota;
  };

  // TODO: Avg. price per shipment is hidden for now
  // const averagePricePerShipment = useMemo(() => {
  //   const price = Number(renderedPrice?.priceAfterDiscount.replace(/[^0-9.]/g, ''));
  //   const quantity = Number(renderQuotaQuantity());
  //   if (isFreePlan || !price || !quantity) {
  //     return null;
  //   }
  //   return (price / quantity).toFixed(4);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [renderedPrice, isFreePlan, quotaRange, totalQuota, quotaPerMonth, plan]);

  const subscriptionText = getSubscriptionText(
    isFreePlan ? Subscription.Monthly : activeSubscription,
    true,
    !isFreePlan && activeSubscription === Subscription.Annually ? 'year' : '',
  );

  return (
    <StyledPlanCard $isHighestPlan={isHighestPlan} $havingPriceTag={havingPriceTag} $smDown={smDown}>
      {havingPriceTag && (
        <div className="plan-tag">
          <Badge tone="info">{camelToRegularText(tag)}</Badge>
        </div>
      )}
      <BlockStack gap="300">
        <BlockStack gap="100">
          <div className="plan-header">
            <div className="plan-title">
              <Text variant="headingXl" as="h6">
                {capitalizeFirstLetter(displayName)}
              </Text>
            </div>
            {plan !== AccountPlan.Starter &&
              getRecommendedPlanByQuota(quotaRange, totalQuota) === plan && (
                <Badge tone="info" size="large" icon={ThumbsUpIcon}>
                  Recommended
                </Badge>
              )}
          </div>
          <div className="plan-des">
            <Text variant="bodyMd" as="p" tone="subdued">
              {description}
            </Text>
          </div>
        </BlockStack>
        {renderedPrice && (
          <div className="plan-price">
            <InlineStack gap="300" align="start" blockAlign="center">
              <Text as="h6" variant="heading2xl">
                {renderedPrice.priceAfterDiscount}
              </Text>
              <BlockStack gap="0">
                {renderedPrice.priceBeforeDiscount &&
                  renderedPrice.discountPercent !== 0 &&
                  plan !== AccountPlan.Starter && (
                    <InlineStack gap="150">
                      <Text as="p" variant="bodyMd" tone="subdued">
                        {renderedPrice.discountCardTitle}
                      </Text>
                      <Text as="p" variant="bodyMd" textDecorationLine="line-through" tone="critical">
                        {renderedPrice.priceBeforeDiscount}
                      </Text>
                    </InlineStack>
                  )}
                <Box paddingBlockStart={renderedPrice.priceBeforeDiscount ? '0' : '200'}>
                  <Text as="p" variant="bodyMd" tone="subdued">
                    {`/${subscriptionText}`}
                  </Text>
                </Box>
              </BlockStack>
            </InlineStack>
          </div>
        )}
        <div className="highlight-quota">
          <div className="highlight-quota-title">
            <Text as="h6" variant="bodyMd" tone="subdued" fontWeight="bold">
              Highlight quota
            </Text>
          </div>
          <div>
            <List type="bullet">
              <List.Item>
                <Text as="p" variant="bodyMd">
                  <strong>{renderQuotaQuantity()}</strong> {` shipments/${subscriptionText}`}
                </Text>
              </List.Item>
            </List>
            {/* {averagePricePerShipment && (
              <Box paddingBlockStart="050" paddingInlineStart="300">
                <Text as="p" variant="bodySm" tone="subdued">
                  {`Avg. $${averagePricePerShipment}/shipment`}
                </Text>
              </Box>
            )} */}
          </div>
          <List type="bullet">
            <List.Item>
              {usagePricePerTracking && usageMaxOverageTracking ? (
                <InlineStack gap="100" blockAlign="center" wrap={false}>
                  <Text as="p" variant="bodyMd">
                    {`$${formatNumberWithFixedDecimals(usagePricePerTracking)} per additional shipment`}
                  </Text>
                  <DescriptionTooltip
                    activator={<Icon source={InfoIcon} tone="subdued" />}
                    content={
                      <Text as="p" variant="bodySm">
                        Shipments beyond your monthly quota are billed automatically at this rate. Shopify shows
                        the exact monthly limit when you approve.
                      </Text>
                    }
                  />
                </InlineStack>
              ) : (
                <Text as="p" variant="bodyMd" tone="subdued">
                  Unavailable after exceeded
                </Text>
              )}
            </List.Item>
          </List>
        </div>
        <div id="button-card" className="footer-item">
          {renderedButtonContainer}
        </div>
        <Box paddingInlineStart="200">
          <BlockStack gap="200">
            <Text variant="headingSm" as="h6" tone="subdued">
              {previousDisplayPlan ? `All in ${previousDisplayPlan}, plus:` : `Features you'll love:`}
            </Text>
            {features.map(({ name, displayName, tooltip, link }, index) => {
              const isReachCS = link.includes('Chat with us');
              const isTooltip = tooltip && !isUpgradePlan;
              const isAIFeature = name === 'ai_analytics_assistant';

              // Don't show AI Analytics Assistant feature if embedded
              if (isAIFeature && CONSTANT.isEmbedded) {
                return null;
              }

              const activatorContent = (
                <div className={`account-benefit padding ${isTooltip && 'tooltip'} ${isAIFeature && 'ai-feature'}`}>
                  <div>
                    {isAIFeature ? (
                      <div
                        style={{
                          width: '1.25rem',
                          height: '1.25rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <AssistantIcon width={11} height={10} color="#7c3aed" />
                      </div>
                    ) : (
                      <Icon source={CheckIcon} tone="success" />
                    )}
                  </div>
                  <div className="plan-card-item">
                    <Text variant="bodyMd" as="h6">
                      {displayName}
                    </Text>
                  </div>
                </div>
              );
              const handleClickLink = () => {
                if (isReachCS) {
                  return handleOpenChat();
                }
                return window.open(link, '_blank');
              };

              return (
                <Fragment key={index}>
                  {!isTooltip ? (
                    activatorContent
                  ) : (
                    <DescriptionTooltip
                      activator={activatorContent}
                      content={
                        <BlockStack gap="100">
                          <Text as="p" variant="bodySm">
                            {tooltip}
                          </Text>
                          {link && (
                            <Link onClick={handleClickLink} removeUnderline>
                              {isReachCS ? link : 'Read document'}
                            </Link>
                          )}
                        </BlockStack>
                      }
                    />
                  )}
                </Fragment>
              );
            })}
          </BlockStack>
        </Box>
      </BlockStack>
    </StyledPlanCard>
  );
};

export default memo(PlanCard);
